import { useCallback, useEffect, useState } from "preact/hooks";

import { completeAssignment, fetchDue, fetchTeamBoard, uncompleteAssignment } from "../lib/api";
import type { DueResponse, DueTask, TeamBoardOut } from "../types";

export interface TeamPiece {
  task: DueTask;
  done: boolean;
}

/** Every child (split-piece) row belonging to a given delegated parent —
 * "who has what, done state" (design-spec-today-card.md §2). Sourced from
 * the already-fetched team-wide `GET /api/v1/due?scope=team` payload
 * rather than a second endpoint call ("unify, don't duplicate" per the
 * A4/A6 task briefs — `parent_assignment_id` is on every `DueTask`). */
export function piecesForParent(teamDue: DueResponse | null, parentId: string): TeamPiece[] {
  if (!teamDue) return [];
  const groups: Array<[DueTask[], boolean]> = [
    [teamDue.overdue, false],
    [teamDue.due_today, false],
    [teamDue.whenever, false],
    [teamDue.done_today, true],
  ];
  const pieces: TeamPiece[] = [];
  for (const [tasks, done] of groups) {
    for (const task of tasks) {
      if (task.parent_assignment_id === parentId) pieces.push({ task, done });
    }
  }
  return pieces;
}

/** Every task (open or done today) belonging to one person — the Team
 * tab's read-only person-detail expand list. */
export function tasksForPerson(teamDue: DueResponse | null, userId: string): TeamPiece[] {
  if (!teamDue) return [];
  const groups: Array<[DueTask[], boolean]> = [
    [teamDue.overdue, false],
    [teamDue.due_today, false],
    [teamDue.whenever, false],
    [teamDue.done_today, true],
  ];
  const rows: TeamPiece[] = [];
  for (const [tasks, done] of groups) {
    for (const task of tasks) {
      if (task.assignee_id === userId) rows.push({ task, done });
    }
  }
  return rows;
}

export function useTeamBoard(enabled: boolean) {
  const [board, setBoard] = useState<TeamBoardOut | null>(null);
  const [teamDue, setTeamDue] = useState<DueResponse | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setError(null);
    try {
      const [boardResult, dueResult] = await Promise.all([
        fetchTeamBoard(),
        fetchDue(undefined, "team"),
      ]);
      setBoard(boardResult);
      setTeamDue(dueResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the team board.");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const completePiece = useCallback(
    async (assignmentId: string) => {
      await completeAssignment(assignmentId, { idempotency_key: crypto.randomUUID() });
      await refresh();
    },
    [refresh],
  );

  const uncompletePiece = useCallback(
    async (assignmentId: string) => {
      await uncompleteAssignment(assignmentId, { idempotency_key: crypto.randomUUID() });
      await refresh();
    },
    [refresh],
  );

  return { board, teamDue, loading, error, refresh, completePiece, uncompletePiece };
}
